import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useLocale } from '../i18n/LocaleContext';

const ScrollablePolicy = ({ text, label, loadingLabel }) => (
  <pre style={{ marginTop: '0.6rem', maxHeight: '180px', overflowY: 'auto', fontSize: '11.5px', color: 'var(--text-secondary)', background: 'var(--bg-2)', padding: '0.75rem', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap' }}>
    {text || loadingLabel}
  </pre>
);

function AuthScreen({ onAuthenticated }) {
  const { t } = useLocale();
  const [mode, setMode] = useState('login'); // login | signup | forgot | reset | verify-email | privacy
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [privacyText, setPrivacyText] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingTenant, setPendingTenant] = useState(null);

  useEffect(() => {
    if (mode === 'signup' || mode === 'privacy') {
      if (!termsText) api.getTerms().then(res => setTermsText(res.text)).catch(() => {});
      if (!privacyText) api.getPrivacyPolicy().then(res => setPrivacyText(res.text)).catch(() => {});
    }
  }, [mode, termsText, privacyText]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) { setResetToken(token); setMode('reset'); }
  }, []);

  const continueAfterAccessChecks = async (tenant) => {
    const emailStatus = await api.emailVerificationStatus();
    if (!emailStatus.verified) {
      setPendingTenant(tenant);
      setMode('verify-email');
      setMessage(t('auth.enterVerification'));
      return;
    }
    const privacyStatus = await api.privacyPolicyStatus();
    if (!privacyStatus.accepted) {
      setPendingTenant(tenant);
      setAcceptedPrivacy(false);
      setMode('privacy');
      return;
    }
    onAuthenticated(tenant);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(''); setMessage(''); setLoading(true);
    try {
      const result = mode === 'login'
        ? await api.login(email, password)
        : await api.signup(email, password, acceptedTerms, acceptedPrivacy, firstName.trim());
      api.setToken(result.token);
      await continueAfterAccessChecks(result.tenant);
      if (mode === 'signup' && !result.verificationEmailSent) setMessage(t('auth.accountCreated'));
    } catch (err) {
      setError(err.response?.data?.error || t('auth.error'));
    } finally { setLoading(false); }
  };

  const handleVerifyEmail = async (event) => {
    event.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.verifyEmailCode(verificationCode);
      const privacyStatus = await api.privacyPolicyStatus();
      if (!privacyStatus.accepted) { setMode('privacy'); return; }
      onAuthenticated(pendingTenant);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.verificationRejected'));
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    setError(''); setLoading(true);
    try {
      await api.requestEmailVerification();
      setMessage(t('auth.codeSent'));
    } catch (err) {
      setError(err.response?.data?.error || t('auth.codeSendFailed'));
    } finally { setLoading(false); }
  };

  const handleAcceptPrivacy = async (event) => {
    event.preventDefault();
    if (!acceptedPrivacy) { setError(t('auth.privacyRequired')); return; }
    setError(''); setLoading(true);
    try {
      await api.acceptPrivacyPolicy();
      onAuthenticated(pendingTenant);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.privacySaveFailed'));
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError(''); setLoading(true);
    try { const result = await api.forgotPassword(email); setMessage(result.message); }
    catch (err) { setError(err.response?.data?.error || t('auth.error')); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.resetPassword(resetToken, newPassword);
      setMessage(t('auth.resetComplete'));
      setMode('login');
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) { setError(err.response?.data?.error || t('auth.error')); }
    finally { setLoading(false); }
  };

  const Header = ({ subtitle }) => <div className="auth-card-header"><div className="brand-mark" style={{ width: 32, height: 32, fontSize: 15 }}>C</div><h1>CodeVoyage.ai</h1><p className="subtitle">{subtitle}</p></div>;

  if (mode === 'forgot') return <div className="auth-screen"><div className="auth-card"><Header subtitle={t('auth.resetPassword')} />{message && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '1rem' }}>{message}</div>}{!message && <form onSubmit={handleForgotPassword}><div className="form-group"><label>{t('auth.email')}</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus /></div>{error && <div className="auth-error">{error}</div>}<button type="submit" className="submit-btn" disabled={loading}>{loading ? t('auth.wait') : t('auth.sendReset')}</button></form>}<button className="auth-switch" onClick={() => { setMode('login'); setMessage(''); setError(''); }}>{t('auth.backToSignIn')}</button></div></div>;

  if (mode === 'reset') return <div className="auth-screen"><div className="auth-card"><Header subtitle={t('auth.choosePassword')} /><form onSubmit={handleResetPassword}><div className="form-group"><label>{t('auth.newPassword')}</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} autoFocus /></div>{error && <div className="auth-error">{error}</div>}<button type="submit" className="submit-btn" disabled={loading}>{loading ? t('auth.wait') : t('auth.reset')}</button></form></div></div>;

  if (mode === 'verify-email') return <div className="auth-screen"><div className="auth-card"><Header subtitle={t('auth.verifyEmail')} /><p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{message || t('auth.enterVerification')}</p><form onSubmit={handleVerifyEmail}><div className="form-group"><label>{t('auth.verificationCode')}</label><input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} required autoFocus /></div>{error && <div className="auth-error">{error}</div>}<button type="submit" className="submit-btn" disabled={loading || verificationCode.length !== 6}>{loading ? t('auth.wait') : t('auth.verify')}</button></form><button className="auth-switch" onClick={resendVerification} disabled={loading}>{t('auth.sendNewCode')}</button></div></div>;

  if (mode === 'privacy') return <div className="auth-screen"><div className="auth-card"><Header subtitle={t('auth.privacyAcceptance')} /><form onSubmit={handleAcceptPrivacy}><div className="form-group"><ScrollablePolicy text={privacyText} label={t('auth.privacy')} loadingLabel={t('auth.loadingPolicy', { label: t('auth.privacy') })} /><label className="checkbox-label"><input type="checkbox" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)} required /><span>{t('auth.acceptPrivacy')}</span></label></div>{error && <div className="auth-error">{error}</div>}<button type="submit" className="submit-btn" disabled={loading || !acceptedPrivacy}>{loading ? t('auth.wait') : t('auth.acceptContinue')}</button></form></div></div>;

  return <div className="auth-screen"><div className="auth-card"><Header subtitle={mode === 'login' ? t('auth.signInAccount') : t('auth.createAccount')} />{message && <div style={{ color: 'var(--text-secondary)', fontSize: '12.5px', marginBottom: '1rem' }}>{message}</div>}<form onSubmit={handleSubmit}>{mode === 'signup' && <div className="form-group"><label htmlFor="signup-first-name">{t('auth.firstName')}</label><input id="signup-first-name" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required maxLength={30} autoComplete="given-name" /></div>}<div className="form-group"><label>{t('auth.email')}</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus /></div><div className="form-group"><label>{t('auth.password')}</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} /></div>{mode === 'signup' && <div className="form-group"><label className="checkbox-label"><input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} required /><span>{t('auth.acceptTerms')} <button type="button" className="auth-switch" style={{ display: 'inline', padding: 0 }} onClick={() => setShowTerms(!showTerms)}>{t('auth.terms')}</button></span></label>{showTerms && <ScrollablePolicy text={termsText} label={t('auth.terms')} loadingLabel={t('auth.loadingPolicy', { label: t('auth.terms') })} />}<label className="checkbox-label" style={{ marginTop: '0.7rem' }}><input type="checkbox" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)} required /><span>{t('auth.acceptPrivacy')} <button type="button" className="auth-switch" style={{ display: 'inline', padding: 0 }} onClick={() => setShowPrivacy(!showPrivacy)}>{t('auth.privacy')}</button></span></label>{showPrivacy && <ScrollablePolicy text={privacyText} label={t('auth.privacy')} loadingLabel={t('auth.loadingPolicy', { label: t('auth.privacy') })} />}</div>}{error && <div className="auth-error">{error}</div>}<button type="submit" className="submit-btn" disabled={loading || (mode === 'signup' && (!acceptedTerms || !acceptedPrivacy))}>{loading ? t('auth.wait') : mode === 'login' ? t('auth.signIn') : t('auth.signUp')}</button></form>{mode === 'login' && <button className="auth-switch" onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}>{t('auth.forgotPassword')}</button>}<button className="auth-switch" onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setMessage(''); }}>{mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}</button></div></div>;
}

export default AuthScreen;
